import React from 'react'
import { Box, Tooltip } from '@mui/material'
import { styled } from '@mui/material/styles'
import { StudioTool } from '../../types'
import { GlassPanel } from '../shared'

export interface StudioToolbarProps {
  tool: StudioTool
  onToolChange: (tool: StudioTool) => void
  onMuse: () => void
  /** A loaded Classic room has a fixed, pre-baked slot count — nowhere procedural to hang a new print. */
  hangDisabled?: boolean
  onUndo?: () => void
  onRedo?: () => void
  canUndo?: boolean
  canRedo?: boolean
}

const Root = styled(GlassPanel)({
  position: 'absolute',
  left: '50%',
  bottom: 16,
  transform: 'translateX(-50%)',
  zIndex: 40,
  display: 'flex',
  alignItems: 'center',
  gap: 3,
  padding: 5,
  borderRadius: 15,
})

const ToolBtn = styled('button', { shouldForwardProp: (p) => p !== '$on' && p !== '$dim' })<{
  $on?: boolean
  $dim?: boolean
}>(({ $on, $dim }) => ({
  all: 'unset',
  width: 38,
  height: 38,
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 15,
  cursor: $dim ? 'default' : 'pointer',
  color: $on ? '#be9dfd' : 'var(--ds-night-ink-subtle)',
  background: $on ? 'rgba(190,157,253,0.18)' : 'transparent',
  opacity: $dim ? 0.32 : 1,
  transition: 'all 140ms ease',
  '&:hover': $dim
    ? undefined
    : {
        background: $on ? 'rgba(190,157,253,0.18)' : 'rgba(244,243,246,0.08)',
        color: $on ? '#be9dfd' : 'var(--ds-night-ink)',
      },
  '& svg': { display: 'block' },
}))

const Sep = styled(Box)({ width: 1, height: 22, background: 'var(--ds-night-border)', margin: '0 4px' })

const MuseBtn = styled('button')({
  all: 'unset',
  height: 38,
  padding: '0 14px',
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  fontSize: 12.5,
  fontWeight: 650,
  cursor: 'pointer',
  color: 'var(--ds-night-ink)',
  '&:hover': { background: 'rgba(190,157,253,0.14)' },
})

const Spark = styled('span')({
  fontSize: 13,
  background: 'var(--ds-gradient-spectrum)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
})

/** Curved arrow — stroke matches the toolbar’s night-ink weight. */
function UndoIcon() {
  return (
    <svg width='18' height='18' viewBox='0 0 18 18' fill='none' aria-hidden>
      <path
        d='M7.25 4.25H4.5v2.75'
        stroke='currentColor'
        strokeWidth='1.6'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M4.65 7A5.75 5.75 0 1 1 5.1 12.4'
        stroke='currentColor'
        strokeWidth='1.6'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

function RedoIcon() {
  return (
    <svg width='18' height='18' viewBox='0 0 18 18' fill='none' aria-hidden>
      <path
        d='M10.75 4.25H13.5v2.75'
        stroke='currentColor'
        strokeWidth='1.6'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M13.35 7A5.75 5.75 0 1 0 12.9 12.4'
        stroke='currentColor'
        strokeWidth='1.6'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

/** Figma UI3-style floating bottom toolbar — history, select/hang/comment, Muse. */
export function StudioToolbar({
  tool,
  onToolChange,
  onMuse,
  hangDisabled,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
}: StudioToolbarProps) {
  return (
    <Root>
      {(onUndo || onRedo) && (
        <>
          <Tooltip title='Undo · ⌘Z' arrow>
            <span>
              <ToolBtn
                type='button'
                $dim={!canUndo}
                disabled={!canUndo}
                onClick={onUndo}
                aria-label='Undo'
              >
                <UndoIcon />
              </ToolBtn>
            </span>
          </Tooltip>
          <Tooltip title='Redo · ⇧⌘Z' arrow>
            <span>
              <ToolBtn
                type='button'
                $dim={!canRedo}
                disabled={!canRedo}
                onClick={onRedo}
                aria-label='Redo'
              >
                <RedoIcon />
              </ToolBtn>
            </span>
          </Tooltip>
          <Sep />
        </>
      )}
      <Tooltip title='Select · V' arrow>
        <ToolBtn $on={tool === 'select'} onClick={() => onToolChange('select')}>
          ➤
        </ToolBtn>
      </Tooltip>
      <Tooltip title={hangDisabled ? 'Classic rooms have a fixed number of print slots' : 'Hang a print · H'} arrow>
        <ToolBtn
          $on={tool === 'hang'}
          onClick={() => !hangDisabled && onToolChange('hang')}
          style={hangDisabled ? { opacity: 0.35, cursor: 'default' } : undefined}
        >
          🖼
        </ToolBtn>
      </Tooltip>
      <Tooltip title='Pin a note · C' arrow>
        <ToolBtn $on={tool === 'comment'} onClick={() => onToolChange('comment')}>
          💬
        </ToolBtn>
      </Tooltip>
      <Sep />
      <Tooltip title='Heuristic re-hang — shuffles pace &amp; eye line. Not an AI model.' arrow>
        <MuseBtn onClick={onMuse}>
          <Spark>✦</Spark>Muse
        </MuseBtn>
      </Tooltip>
    </Root>
  )
}
